import { ElementRef, Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import * as CryptoJS from 'crypto-js';
import { MoreOptionsModalComponent } from '@shared/components/modal/more-options-modal/more-options-modal.component';
import { IQuery } from '../../interfaces/query';
import { IFilterSelection } from '../../components/filter/interfaces/filter-selection';
import * as moment from 'moment';
import { environment } from '@environments/environment';
import { GRID_OPTIONS, VIEW_BAR_OPTIONS_PREV_VALUES } from '../../constants/localstorage-keys';
import { EOptionType } from '../../components/shared-viewbar/viewbar-options/viewbar-option-type.enum';
import { FileTypes } from '../../constants/file-types';
import { TranslateService } from '@ngx-translate/core';
import { Title } from '@angular/platform-browser';
import * as _ from 'lodash';
import { DataService } from '../data/data.service';
import { SessionService } from '../session/session.service';
import { AbstractControl, ValidatorFn } from '@angular/forms';
import { Operation } from 'fast-json-patch';
import { FieldType } from '@app/admin-station/work-center/scheduling/constants/schedule-form-data';

@Injectable({
  providedIn: 'root',
})
export class UtilService {
  private static key = CryptoJS.enc.Hex.parse(environment.encryptionKey);
  private static iv = CryptoJS.enc.Hex.parse(environment.encryptionIV);
  public static numbersRegex = new RegExp(/[^0-9]*/g);

  constructor(
    private dataService: DataService,
    private translateService: TranslateService,
    private titleMetaTagService: Title,
    private sessionService: SessionService
  ) { }

  DBLocalStorage = new BehaviorSubject<any>(null);
  getDBLocalStorage = this.DBLocalStorage.asObservable();
  setDBLocalStorage(DBLocalStorage: any): void {
    this.DBLocalStorage.next(DBLocalStorage);
  }

  public mirrorObjects(object1: Record<string, any>, object2: Record<string, any>) {
    const recursiveMirror = (target: Record<string, any>, source: Record<string, any>) => {
      _.forIn(target, (value, key) => {
        if (_.isObject(value) && _.isObject(source[key])) {
          target[key] = recursiveMirror(value, source[key]);
        } else {
          target[key] = source[key];
        }
      });
      return target;
    };
    const mirroredObject2 = _.pick(object2, _.keys(object1));
    return recursiveMirror(_.cloneDeep(mirroredObject2), object1);
  }

  public setBrowserTabTitle(pageTitle: string) {
    const title = this.translateService.instant(pageTitle);
    this.titleMetaTagService.setTitle(title);
  }

  public setSessionFromPreferences(preferences: any[], userId: string): void {
    const dbKey = `${GRID_OPTIONS}:${userId}`;

    if (preferences && userId) {
      const prefConfig = preferences.filter((v) => String(UtilService.decryptData(v.key)).startsWith(dbKey)) || [];

      const gridConfigs = this.getMaxValueFromPreferences(prefConfig);

      for (const config of gridConfigs) {
        const key = String(UtilService.decryptData(config.key)).substring(dbKey.length + 1);
        this.sessionService.removeItem(key);
        if (config.value) this.sessionService.setItem(key, JSON.parse(config.value));
      }
    }
  }

  private getMaxValueFromPreferences(gridConfigs: any[]): { key: string; value: any }[] {
    const newestConfigsByKey: Record<string, any> = {};
    gridConfigs.forEach((config) => {
      const { key, updatedDate, value } = config;
      if (!(key in newestConfigsByKey) || updatedDate > newestConfigsByKey[key]) {
        newestConfigsByKey[key] = value;
      }
    });

    const newestConfigs = Object.keys(newestConfigsByKey).map((key) => {
      return { key, value: newestConfigsByKey[key] };
    });

    return newestConfigs;
  }

  public static openMoreOptionsModal(dialog: any, actions: any[], title: string): Observable<any> {
    return dialog
      .open(MoreOptionsModalComponent, {
        width: '100vw',
        maxHeight: 'none',
        maxWidth: 'none',
        data: {
          actions,
          title,
        },
      })
      .afterDismissed();
  }

  public static getElementMaxHeightByParent(parentClass: string, selectedClass: string): string {
    const parentElement = document.querySelector(parentClass) as HTMLDivElement;
    const selectedElement = document.querySelector(selectedClass) as HTMLDivElement;
    const height = parentElement.offsetHeight - selectedElement.offsetTop;
    return `${height}px`;
  }

  public static isMobileAndTabletSize(): boolean {
    return window.innerWidth < 1024;
  }

  public static isPhoneSize(): boolean {
    return window.innerWidth <= 600;
  }

  public static isTabletSize(): boolean {
    return window.innerWidth < 1024 && window.innerWidth > 600;
  }

  public static isSmallDesktopSizeOrLess(): boolean {
    return window.innerWidth <= 1440;
  }

  public static timeOut(ms: number): Promise<boolean> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  public static fillUrl(queryParams: IQuery, additionalParams?: any): string {
    const params = { ...queryParams, ...additionalParams };

    let url = '?';
    if (params) {
      for (const key of Object.keys(params)) {
        url += `${key}=${params[key]}&`;
      }
    }

    return url.slice(0, -1);
  }

  public static getEmailPattern(): RegExp {
    // TLD upper bound was 5 chars, rejecting valid modern gTLDs like ".solutions" (9 chars);
    // 24 covers the longest current TLD (".international").
    return new RegExp(/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,24}$/);
  }

  public static multipleEmailsValidator(): ValidatorFn {
    const emailPattern = new RegExp(this.getEmailPattern());
    return (control: AbstractControl): { [key: string]: any } | null => {
      if (!control.value) {
        return null;
      }
      const rawEmails: string[] = Array.isArray(control.value)
        ? control.value
        : String(control.value).split(',');
      const emails = rawEmails.map((email: string) => email.trim()).filter((email: string) => email.length > 0);
      const invalidEmails = emails.filter((email: string) => !emailPattern.test(email));
      return invalidEmails.length > 0 ? { invalidEmails: invalidEmails } : null;
    };
  }

  public static getAlphanumericPattern(): RegExp {
    return new RegExp(/^[a-zA-Z0-9]*$/);
  }

  public static getValidationErrorMessage(errors: { validations: { message: string }[] }): string {
    let message = '';
    if (errors.validations) {
      errors.validations.forEach((v: Record<string, any> & { message: string }) => {
        message = `${v.message}\n`;
      });
    }

    return message;
  }

  public static capitalizeFirstLetter(string: string) {
    return string.charAt(0).toUpperCase() + string.slice(1);
  }

  public static highlightSearchText(text: string, searchValue: string) {
    if (!text) {
      return '';
    }

    let keywords: string[] = [];
    const transformSpecialCharactersInRegExp = (string: string) => {
      return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    };

    if (searchValue?.length) {
      keywords = searchValue.split(' ');
    }
    const escapedKeywords = keywords.map(transformSpecialCharactersInRegExp);
    const pattern = new RegExp(`(${escapedKeywords.join('|')})`, 'ig');

    return String(text).replace(pattern, (match) => `<span class="highlight">${match}</span>`);
  }

  public static isSameObject(first: any, second: any): boolean {

    // Use lodash deep equality for robust comparison
    return _.isEqual(first, second);
  }

  public static getFilterConfiguration(module: string, filters: IFilterSelection): any {
    let filterConfig: any;
    switch (module) {
      case 'crib':
        filterConfig = {
          hierarchy: 'orgId',
          checklist: '_id',
          cribTag: 'tags._id',
          locationTag: 'location.tags._id',
        };
        break;
      case 'user':
        filterConfig = {
          hierarchy: 'orgId',
          listType: 'roles._id',
        };
        break;
      case 'orders':
        filterConfig = {
          checklist: 'crib._id',
          binsChecklist: 'bin._id',
          itemsChecklist: 'item._id',
          poChecklist: 'po._id',
          supplierChecklist: 'supplier._id',
          statusChecklist: 'status',
        };
        break;
      case 'item':
        filterConfig = {
          tag: 'tags._id',
        };
        break;
      case 'restrictions':
        filterConfig = {
          hierarchy: 'orgId',
          checklist: 'cribs._id',
          cribGroups: 'cribGroups._id',
          userGroups: 'userGroups._id',
        };
        break;
      case 'userGroups':
        filterConfig = {
          hierarchy: 'orgId',
        };
        break;
      case 'notification':
        filterConfig = {
          date: 'dateRange',
        };
        break;
      default:
        break;
    }

    const selections: any = {};

    for (const filterKey of Object.keys(filterConfig)) {
      const selectionKey = filterConfig[filterKey];

      if (
        filterKey === 'itemsChecklist' ||
        filterKey === 'checklist' ||
        filterKey === 'binsChecklist' ||
        filterKey === 'poChecklist' ||
        filterKey === 'supplierChecklist' ||
        (filterKey === 'statusChecklist' && filters[filterKey]?.length > 0)
      ) {
        selections[selectionKey] = filters[filterKey]?.map((v: { value: { _id: string } }) => v.value._id).join(',');
      } else if (filterKey === 'date') {
        selections[selectionKey] =
          filters[filterKey]?.length > 0
            ? `{$gte:ISODate('${filters[filterKey][0]._id}'),$lt:ISODate('${filters[filterKey][1]._id}')}`
            : '';
      } else if (filters[filterKey]?.length > 0) {
        selections[selectionKey] = filters[filterKey]?.map((v: { _id: string }) => v._id).join(',');
      }
    }

    return selections;
  }

  public static getDateTimeByString(dateValue: string, format?: string) {
    const dateFormat = format || 'MM/DD/YYYY hh:mm:ss A';

    if (!dateValue || dateValue.includes('0001-01-01') || dateValue.includes('1900-01-01')) {
      return '-';
    } else {
      const systemFormat = 'YYYY-MM-DD HH:mm:ssZZZ';
      const message = moment(dateValue, systemFormat).format(dateFormat);
      return message;
    }
  }

  public static formatDate(createdDate: string, isMobile?: boolean) {
    const date = new Date(createdDate);
    if (new Date().toDateString() === date.toDateString()) {
      return `${date.getHours() % 12 || 12}:${String(date.getMinutes()).padStart(2, '0')} ${date.getHours() >= 12 ? 'PM' : 'AM'
        }`;
    } else {
      if (isMobile) {
        return `${date.toLocaleString('default', {
          month: 'short',
        })} ${date.getDate()}`;
      } else {
        return `${date.toLocaleString('default', {
          month: 'long',
        })} ${date.getDate()}`;
      }
    }
  }

  private getUtilList<T>(type: 'enums' | 'fields', name: string, cachedKey: string): Observable<T[]> {
    const url = `Utils/${type}/${name}`;

    return this.dataService.get<T[]>(url);
  }

  getUtilEnumList<T>(enumName: string): Observable<T[]> {
    return this.getUtilList('enums', enumName, `ENUM_${enumName}`);
  }

  getUtilFieldsByModelName(modelName: string): Observable<FieldType[]> {
    return this.getUtilList<FieldType>('fields', `${modelName}_model`, `FIELDS_${modelName}`);
  }

  getHeaderFilterEnum(enumName: string, langPath = 'string') {
    return this.getUtilEnumList(enumName).pipe(
      map((x) => {
        return x.map((x: any) => {
          const response = {
            name: this.translateService.instant(`${langPath}.${x?.toLowerCase().replace(/\s/g, '') ?? x}`),
            value: x === 'OrderPoint' ? 'Order Point' : x,
          };
          return response;
        });
      })
    );
  }

  getAPICachedValue(prefix: any, key: any) {
    const url = 'Utils/getcachevalue';
    return this.dataService.get(`${url}?prefix=${prefix}&key=${key}`).pipe(map((response: Response | any) => response));
  }

public static encryptData(data: any) {
    try {
      const stringified = typeof data === 'string' ? data : JSON.stringify(data);
      if (!environment.production) {
        return stringified;
      }
      return CryptoJS.AES.encrypt(stringified, UtilService.key, {
        iv: UtilService.iv,
      }).toString();
    } catch (e) {
      return '';
    }
  }

  public static decryptData(data: any, forceDecrypt?: boolean) {
    if (!environment.production && !forceDecrypt) {
      return data;
    }
    try {
      const bytes = CryptoJS.AES.decrypt(data, UtilService.key, {
        iv: UtilService.iv,
      });
      if (bytes.toString()) {
        return bytes.toString(CryptoJS.enc.Utf8);
      }
      return '';
    } catch (e) {
      return '';
    }
  }

  static async downloadBlobFile(url: Blob, fileName: string, extension: string): Promise<void> {
    const fileURL = window.URL.createObjectURL(url);
    const anchor = document.createElement('a');
    // Only append extension if fileName does not already end with it
    const normalizedExtension = extension.startsWith('.') ? extension : `.${extension}`;
    const downloadName = fileName.toLowerCase().endsWith(normalizedExtension.toLowerCase())
      ? fileName
      : `${fileName}${normalizedExtension}`;
    anchor.download = downloadName;
    anchor.href = fileURL;
    anchor.click();
    await UtilService.timeOut(100);
    anchor.remove();
  }

  isNullOrEmpty(value: any): boolean {
    return value === undefined || value === null || value === '';
  }

  //
  //Convert hex code to rgba style
  //
  hexToRGB(hex: string, alpha?: number): string | undefined {
    if (!hex) {
      return;
    }
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);

    return 'rgba(' + r + ', ' + g + ', ' + b + (alpha ? ', ' + alpha : '') + ')';
  }

  //
  //Return an array without duplicated elements
  //
  arrayUnique<T extends Record<string, any>>(array: T[], field?: string) {
    const a = array.filter(Boolean).concat();
    for (let i = 0; i < a.length; ++i) {
      for (let j = i + 1; j < a.length; ++j) {
        if ((field && a[i][field] === a[j][field]) || a[i] === a[j]) {
          a.splice(j--, 1);
        }
      }
    }

    return a;
  }

  //
  // remove a string from a comma delimited string
  // e.i: removeValueFromCommaDelimited('a,b,c','b'); ==> a,c
  //
  removeValueFromCommaDelimited(list: string, value: string, separator?: string) {
    separator = separator || ',';
    const values = list.split(separator);
    for (let i = 0; i < values.length; i++) {
      if (values[i] === value) {
        values.splice(i, 1);
        return values.join(separator);
      }
    }
    return list;
  }

  public static parseNumber(value: string | number): number {
    if (isNaN(Number(value))) {
      const cleanedString = (value as string).replace(UtilService.numbersRegex, '');
      return Number(cleanedString);
    }
    return Number(value);
  }

  public static hasDecimal(number: number): number {
    const numberString = number.toString();
    // Check if the number has a decimal point
    if (numberString.indexOf('.') !== -1) {
      return numberString.split('.')[1].length;
    } else {
      return 0;
    }
  }

  public static textToUrl(text: string, config: { text: string; url: string }) {
    const pattern = new RegExp(`${config?.text}`, 'ig');
    return text.replace(
      pattern,
      (match) => `
        <a
          target="_blank"
          class="url" href="${config?.url}"
        >
          ${match}
        </a>
      `
    );
  }

  public static generateCurrentViewPrefix(): string {
    const route = window.location.href.split('?')[0];
    const currentView = route.split('/').pop()?.toUpperCase();
    return `${currentView}:${VIEW_BAR_OPTIONS_PREV_VALUES}`;
  }

  public static getSessionKey(optionType: EOptionType): string {
    return `${UtilService.generateCurrentViewPrefix()}:${optionType}`;
  }

  downloadFile(response: any, fileName: string, fileType: keyof typeof FileTypes) {
    const blob = new Blob([response], { type: FileTypes[fileType] });
    const blobURL = URL.createObjectURL(blob);

    const a = document.createElement('a');
    a.href = blobURL;
    a.download = fileName;

    document.body.appendChild(a);
    a.click();

    a.remove();
    URL.revokeObjectURL(blobURL);
  }

  public static highlightSearchHtml(text: string, searchValue: string) {
    if (!text) {
      return '';
    }

    function replaceHtmlTagsWithIdentifiers(text: string) {
      const tagRegex = /<[^>]+>/g;
      let identifierCount = 0;
      const placeholders: { identifier: string; match: string }[] = [];

      const strippedText = text.replace(tagRegex, (match) => {
        const identifier = `<!--${identifierCount}-->`;
        placeholders.push({ identifier, match });
        identifierCount++;
        return identifier;
      });

      return { strippedText, placeholders };
    }

    function restoreHtmlTags(textAndPlaceholders: { strippedText: string; placeholders: any[] }): string {
      const { strippedText, placeholders } = textAndPlaceholders;

      return placeholders.reduce((acc, { identifier, match }) => {
        return acc.replace(identifier, match);
      }, strippedText);
    }

    const hasHtml = /<\/?[a-z][\s\S]*>/i.test(text);
    const { strippedText, placeholders }: any = hasHtml ? replaceHtmlTagsWithIdentifiers(text) : text;
    let keywords: string[] = [];
    if (searchValue) {
      keywords = searchValue.split(' ');
    }

    const pattern = new RegExp(`(${keywords.join('|')})`, 'ig');
    const highlightedText = strippedText
      ? strippedText.replace(pattern, (match: string) => `<span class="highlight">${match}</span>`)
      : '';

    const finalText = hasHtml ? restoreHtmlTags({ strippedText: highlightedText, placeholders }) : highlightedText;
    return finalText;
  }

  public static labelValueSanitization(input: string): string {
    if (!input) {
      return '';
    }
    let sanitized = input.toLowerCase();

    // 1. Replace spaces with underscores
    sanitized = sanitized.replace(/\s+/g, '_');

    // 2. Remove all characters that are NOT alphanumeric or underscore
    sanitized = sanitized.replace(/[^a-z0-9_]/g, '');

    // 3. Ensure numeric characters do not appear at the beginning
    sanitized = sanitized.replace(/^[0-9]+/, ''); ///^[0-9]+/

    return sanitized;
  }

  public static fileNameSanitization(fileName: string) {
    const lastDotIndex = fileName.lastIndexOf('.');
    const name = fileName.substring(0, lastDotIndex);
    const extension = fileName.substring(lastDotIndex);
    const sanitizedNamePart = name.replace(/[^\w]/g, '');
    const sanitizedFileName = sanitizedNamePart + extension;
    return sanitizedFileName;
  }

  public static getOrdinalNumber(number: number): {
    full: string | null;
    short: string;
  } {
    let ordinal: { full: string | null; short: string } = { full: null, short: 'th' };

    switch (number) {
      case 1:
      case 21:
      case 31:
        ordinal = { full: 'string.first', short: 'st' };
        break;
      case 2:
      case 22:
        ordinal = { full: 'string.second', short: 'nd' };
        break;
      case 3:
      case 23:
        ordinal = { full: 'string.third', short: 'rd' };
        break;
      case 4:
        ordinal = { full: 'string.fourth', short: 'th' };
        break;
      default:
        break;
    }

    return ordinal;
  }

  public static getDayName(day: number): string {
    switch (day) {
      case 0:
        return 'week.sunday';
      case 1:
        return 'week.monday';
      case 2:
        return 'week.tuesday';
      case 3:
        return 'week.wednesday';
      case 4:
        return 'week.thursday';
      case 5:
        return 'week.friday';
      case 6:
        return 'week.saturday';
      default:
        return '';
    }
  }

  public static getMonthName(month: number): string {
    switch (month) {
      case 0:
        return 'week.january';
      case 1:
        return 'week.february';
      case 2:
        return 'week.march';
      case 3:
        return 'week.april';
      case 4:
        return 'week.may';
      case 5:
        return 'week.june';
      case 6:
        return 'week.july';
      case 7:
        return 'week.august';
      case 8:
        return 'week.september';
      case 9:
        return 'week.october';
      case 10:
        return 'week.november';
      case 11:
        return 'week.december';
      default:
        return '';
    }
  }

  public static formatTimeSpan(timeSpan: string): String {
    // Parse the input string to extract hours, minutes, seconds, and milliseconds
    const [hours, minutes, secondsWithMilliseconds] = timeSpan.split(':');

    if (+hours <= 0 && +minutes <= 0 && +secondsWithMilliseconds <= 0) {
      return '';
    }
    // Convert hours, minutes, and seconds to numbers
    const parsedHours = +hours * 60;
    const parsedMinutes = +minutes * 60;
    const parsedSeconds = +secondsWithMilliseconds;

    // Calculate the total seconds
    const totalSeconds = parsedHours + parsedMinutes + parsedSeconds;

    // Return the formatted time span with two decimal places
    return totalSeconds.toFixed(4) + ' seconds';
  }

  public static twelveHoursFormatSpan(timeSpan: string): String {
    // Parse the input string to extract hours, minutes, seconds, and milliseconds
    const parts = timeSpan.split(':');

    if (parts.length < 2) {
      return '';
    }

    let [hours, minutes] = parts;

    const period = +hours >= 12 ? 'PM' : 'AM';
    hours = `${+hours % 12 || 12}`;
    const formatedHour = hours.length < 2 ? '0' + hours : hours;

    return `${formatedHour}:${minutes} ${period}`;
  }

  public static convertToMilitaryTime(time: string): {
    militaryTime: string;
    formattedTime: string;
  } {
    const parts = time.split(' ');
    if (parts.length !== 2) {
      throw new Error('Invalid time format');
    }

    const timeString = parts[0];
    const period = parts[1].toUpperCase();
    const timeParts = timeString.split(':');

    if (timeParts.length !== 2) {
      throw new Error('Invalid time format');
    }

    let hours = parseInt(timeParts[0]);
    const minutes = timeParts[1];

    if ((period === 'PM' || period === 'pm') && hours !== 12) {
      hours += 12;
    } else if ((period === 'AM' || period === 'am') && hours === 12) {
      hours = 0;
    }

    const militaryTime = hours.toString().padStart(2, '0') + ':' + minutes + ':00';
    let formattedTime = timeParts[0].padStart(2, '0') + ':' + minutes;
    formattedTime = `${formattedTime} ${period}`;
    return { militaryTime, formattedTime };
  }

  public static convertFromMilitaryTime(militaryTime: string): string {
    const timeParts = militaryTime.split(':');
    const hours = parseInt(timeParts[0]);
    const minutes = timeParts[1];
    const period = hours >= 12 ? 'PM' : 'AM';
    const formattedHours = (hours % 12 || 12).toString().padStart(2, '0');
    return `${formattedHours}:${minutes} ${period}`;
  }

  public static removeNulls(obj: Record<string, any>): any {
    return Object.keys(obj).reduce((acc: Record<string, any>, key: string) => {
      if (obj[key] !== null) acc[key] = obj[key];
      return acc;
    }, {});
  }

  public static async hashValue(value: string): Promise<string> {
    if (!value) {
      return '';
    }

    // Convert value to Uint8Array
    const valueBuffer = new TextEncoder().encode(value);

    // Generate salt
    const salt = crypto.getRandomValues(new Uint8Array(16));

    // Derive key from value and salt
    const key = await crypto.subtle.importKey('raw', valueBuffer, { name: 'PBKDF2' }, false, ['deriveBits']);

    // Derive bits using PBKDF2
    const derivedKey = await crypto.subtle.deriveBits(
      {
        name: 'PBKDF2',
        salt: salt,
        iterations: 1000,
        hash: 'SHA-256',
      },
      key,
      256 // Output key size in bits
    );

    // Concatenate salt and derived key
    const combinedBuffer = new Uint8Array(salt.length + derivedKey.byteLength);
    combinedBuffer.set(salt, 0);
    combinedBuffer.set(new Uint8Array(derivedKey), salt.length);

    // Convert combinedBuffer to base64 string
    const base64String = btoa(String.fromCharCode.apply(null, [...combinedBuffer]));

    return base64String;
  }

  transformZeroNumberValuesToString(value: any): any {
    if (value === null || value === undefined) {
      return value;
    }

    if (typeof value === 'object') {
      return Array.isArray(value)
        ? value.map(this.transformZeroNumberValuesToString)
        : Object.entries(value).reduce((acc: Record<string, any>, [key, val]) => {
          acc[key] = this.transformZeroNumberValuesToString(val);
          return acc;
        }, {});
    }

    return value === 0 ? '0' : value;
  }

  public static patchModification(patch: Operation[], path: string, value: any): any[] {
    if (patch.some((e) => e.path.startsWith(path))) {
      patch = patch.filter((i) => !i.path.startsWith(path));
      const newPatch: Operation = { op: 'replace', path, value };
      patch.push(newPatch);
    }
    return patch;
  }

  public static createBadgeIfPossible(elementRef: ElementRef, originalArray: any, ALLOWED_ROWS = 2): any[] {
    if (!elementRef.nativeElement || !originalArray || !originalArray.length) return originalArray;

    const MATERIAL_CHIPS_MARGIN = 8;
    const blockWidth = elementRef.nativeElement.clientWidth;
    const chipsWidth = Array.from(elementRef.nativeElement.children[0].children[0].children).map(
      (value: any) => value.clientWidth + MATERIAL_CHIPS_MARGIN
    );

    if (chipsWidth.every((value) => value === MATERIAL_CHIPS_MARGIN)) return originalArray;

    const resultantArray = [];
    let acc = 0;

    for (let i = 0; i < ALLOWED_ROWS; i++) {
      for (let j = 0; j < chipsWidth.length; j++) {
        if (acc + chipsWidth[j] > blockWidth || resultantArray.length >= chipsWidth.length) {
          acc = 0;
          break;
        }
        acc += chipsWidth[resultantArray.length];
        resultantArray.push(originalArray[resultantArray.length]);
      }
    }

    if (JSON.stringify(originalArray) !== JSON.stringify(resultantArray)) {
      resultantArray[resultantArray.length - 1] = {
        name: `+${originalArray.length - resultantArray.length + 1}`,
        _id: '',
        tooltip: originalArray
          .filter((_: any, index: number) => index >= resultantArray.length - 1)
          .map((value: { name: string }) => value.name)
          .join('\n'),
      };
    }

    return resultantArray;
  }

  public static convertHtmlToText(value: string): string {
    const hasHtmlTags = /<[^>]+>/; // regex to check if there's html tags
    const containsHtml = typeof value === 'string' && hasHtmlTags.test(value);
    if (containsHtml) {
      value = value.replace(/<[^>]+>/g, ''); //remove all html tags and attributes
    }
    return value;
  }

  /**
   * Download a file from the backend
   * @param fileName The name of the file to download.
   * @returns Observable<void>
   */
  downloadFileFromBackend(fileName: string): Observable<void> {
    const url = `utils/file?fileName=${encodeURIComponent(fileName)}`;
    return this.dataService.getFile(url).pipe(
      map((blob: Blob) => {
        // Try to extract extension from fileName
        const lastDot = fileName.lastIndexOf('.');
        const extension = lastDot !== -1 ? fileName.substring(lastDot + 1) : 'bin';
        UtilService.downloadBlobFile(blob, fileName, extension);
      })
    );
  }
}
